// AI Meal Plan — Today's plan built from the user's actual logged food
// history and inferred eating window. Display-only; no auto-log, no
// drafts. Lives as a sub-tab in the Calories view.
//
// Storage: sirat.hayah.mealPlan.<YYYY-MM-DD> JSON, cap last 7 days.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { callGemini } from './geminiAI';
import { loadAITarget } from './AICalorieTarget';
import AICostBadge from './hayah/AICostBadge';
import { publishSources, mealPlanSources } from './hayah/pipelineSources';
import AIAnalysingPipeline from './hayah/AIAnalysingPipeline';
import { hapticHayahSuccess, hapticError } from '../hayahHaptics';

const PREFIX = 'sirat.hayah.mealPlan.';
const KEEP_DAYS = 7;

const SYSTEM_PROMPT = `You are a meal planner that matches plans to the user's actual eating habits, not generic templates.

You are planning the REMAINING food for today, not the whole day. The user has already eaten "consumed_today" and you must fill ONLY the "remaining_target" gap. If remaining_target.kcal <= 200 OR remaining_target.protein_g <= 10, return a single small top-up meal (or zero meals if both are already met) and explain why in the rationale.

Hard constraints:
- 0-4 meals total. Default to 2-3 unless the remaining_target is small (then 1) or large (then 4). NOT labelled breakfast / lunch / dinner — the user eats flexibly and often fasts long. Use neutral labels like "Next meal", "Meal 2", "Meal 3", "Optional top-up".
- Total kcal within +-5% of remaining_target.kcal (NOT macro_target.kcal — that's the full-day figure for context).
- Total protein within +-5% of remaining_target.protein_g (most important — protect lean mass).
- >=70% of plan kcal must come from items in top_foods. If you need to introduce a non-top food, it must be commonly available in the user's region and not exotic.
- The FIRST suggested meal's time must be at or AFTER current_time. Do not plan meals that have already passed. Respect the inferred eating_window: the last meal must be at or shortly before eating_window.end.
- If training_day = true, place the highest-carb meal in the post-workout window.
- Halal-only. SA region — assume access to standard SA supermarket items.
- NEVER suggest beverages as part of a meal — not coffee, tea, cappuccino, espresso, lattes, or any hot/cold drink. Drinks are logged separately and are not food. If a beverage appears in top_foods, ignore it.
- NEVER suggest junk food, even if it appears in top_foods. Junk food includes: soft drinks / fizzy drinks (Coca-Cola, Pepsi, Fanta, etc.), sweets, chocolates, candy, crisps, chips, biscuits, pastries, doughnuts, fast food, fried takeaways, energy drinks, flavoured milk drinks with added sugar, or any item whose primary macros are sugar + fat with no meaningful protein. If the user's top_foods list contains junk, ignore those items entirely and fill the gap with real food.

Soft constraints:
- Lean into the user's actual patterns. If they always have the same first meal, keep it. If their top foods cluster into combinations they've eaten together, reuse those combinations.
- Avoid suggesting more than one "cooked from scratch" meal — most days that's unrealistic.

Output: JSON only, no prose, no markdown fences.

Cite the user's actual numbers in the rationale (e.g. "Window honors your 13:42 avg first meal over last 30d").

Tone: direct, quantitative, no fluff.`;

const SCHEMA = {
  type: 'object',
  properties: {
    meals: {
      type: 'array',
      minItems: 0, maxItems: 4,
      items: {
        type: 'object',
        properties: {
          label: { type: 'string' },
          suggested_time: { type: 'string' },
          kcal: { type: 'number' },
          protein_g: { type: 'number' },
          carbs_g: { type: 'number' },
          fat_g: { type: 'number' },
          items: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                qty: { type: 'string' },
                kcal: { type: 'number' },
                familiar: { type: 'boolean' },
              },
              required: ['name','qty','kcal','familiar'],
            },
          },
          rationale: { type: 'string' },
        },
        required: ['label','suggested_time','kcal','protein_g','carbs_g','fat_g','items','rationale'],
      },
    },
    eating_window: {
      type: 'object',
      properties: {
        start: { type: 'string' },
        end: { type: 'string' },
        window_hours: { type: 'number' },
        fast_hours: { type: 'number' },
      },
      required: ['start','end','window_hours','fast_hours'],
    },
    totals: {
      type: 'object',
      properties: {
        kcal: { type: 'number' },
        protein_g: { type: 'number' },
        carbs_g: { type: 'number' },
        fat_g: { type: 'number' },
      },
      required: ['kcal','protein_g','carbs_g','fat_g'],
    },
    macro_fit_pct: { type: 'number' },
    familiar_pct: { type: 'number' },
    rationale: { type: 'string' },
  },
  required: ['meals','eating_window','totals','macro_fit_pct','familiar_pct','rationale'],
};

// ── Data extraction ──────────────────────────────────────────────────────
// Recency-weighted frequency: count * exp(-days_since_last / 30). Half-life
// ~21d so a food eaten 21 days ago counts half as much as one eaten today.
// Slots that should never appear as meal plan suggestions.
// 'drinks' covers coffee, tea, cappuccino, water, shakes, etc.
// 'supplements' covers vitamins, creatine, etc. — not real food.
const EXCLUDED_SLOTS = new Set(['drinks', 'supplements']);

// Name-based beverage guard — catches logged drinks that were routed to
// the meal or snacks slot by mistake.
const BEVERAGE_RE = /coffee|cappuccino|espresso|latte|americano|macchiato|flat\s?white|tea\b|chai|rooibos|milo|horlicks|ovomaltine|hot\s?choc|cocoa|shake|smoothie|juice\b|cooldrink|fizzy|soda\b|cola\b|coke\b|sprite\b|fanta\b|energy\s?drink|red\s?bull|monster\b|powerade|gatorade|milk\s?drink|dairy\s?blend|protein\s?drink|nesquik/i;

export function extractTopFoods(calorieLog, days = 90, n = 15) {
  const cutoff = Date.now() - days * 86400000;
  const counts = new Map();
  Object.entries(calorieLog || {}).forEach(([date, day]) => {
    const t = new Date(date).getTime();
    if (isNaN(t) || t < cutoff) return;
    const meals = day?.meals || {};
    Object.entries(meals).forEach(([slot, arr]) => {
      if (EXCLUDED_SLOTS.has(slot)) return;          // skip drinks / supplements
      (arr || []).forEach(e => {
        if (!e?.name) return;
        if (BEVERAGE_RE.test(e.name)) return;         // skip beverages in wrong slot
        const k = e.name.toLowerCase().trim();
        if (!k) return;
        const prev = counts.get(k);
        if (prev) {
          prev.count++;
          prev.lastSeen = Math.max(prev.lastSeen, t);
          // average macros across instances (foods can vary by serving)
          prev.kcal = (prev.kcal * (prev.count - 1) + (e.calories || 0)) / prev.count;
          prev.p = (prev.p * (prev.count - 1) + (e.protein || 0)) / prev.count;
          prev.c = (prev.c * (prev.count - 1) + (e.carbs || 0)) / prev.count;
          prev.f = (prev.f * (prev.count - 1) + (e.fat || 0)) / prev.count;
        } else {
          counts.set(k, {
            name: e.name.trim().slice(0, 60),
            count: 1, lastSeen: t,
            kcal: e.calories || 0,
            p: e.protein || 0, c: e.carbs || 0, f: e.fat || 0,
            servingLabel: e.servingLabel || null,
          });
        }
      });
    });
  });
  const now = Date.now();
  return [...counts.values()].map(x => ({
    ...x, score: x.count * Math.exp(-((now - x.lastSeen) / 86400000) / 30),
  })).sort((a, b) => b.score - a.score).slice(0, n).map(x => ({
    name: x.name,
    typical_serving: x.servingLabel || null,
    kcal: Math.round(x.kcal),
    protein_g: +x.p.toFixed(1),
    carbs_g: +x.c.toFixed(1),
    fat_g: +x.f.toFixed(1),
    times_logged: x.count,
  }));
}

export function extractEatingWindow(calorieLog, days = 30) {
  const cutoff = Date.now() - days * 86400000;
  const dayWindows = [];
  Object.entries(calorieLog || {}).forEach(([date, day]) => {
    const t = new Date(date).getTime();
    if (isNaN(t) || t < cutoff) return;
    const meals = day?.meals || {};
    const times = [];
    Object.values(meals).forEach(arr => {
      (arr || []).forEach(e => {
        if (!e?.loggedAt) return;
        const dt = new Date(e.loggedAt);
        if (isNaN(dt)) return;
        times.push(dt.getHours() + dt.getMinutes() / 60);
      });
    });
    if (times.length >= 1) {
      dayWindows.push({ first: Math.min(...times), last: Math.max(...times) });
    }
  });
  if (dayWindows.length < 5) return null;
  const avg = arr => arr.reduce((a, b) => a + b, 0) / arr.length;
  const firstAvg = avg(dayWindows.map(d => d.first));
  const lastAvg  = avg(dayWindows.map(d => d.last));
  const fmt = h => {
    const hh = Math.floor(h);
    const mm = Math.round((h - hh) * 60);
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };
  return {
    first_meal_avg: fmt(firstAvg),
    last_meal_avg: fmt(lastAvg),
    window_hours: +(lastAvg - firstAvg).toFixed(1),
    fast_hours: +(24 - (lastAvg - firstAvg)).toFixed(1),
    days_analyzed: dayWindows.length,
  };
}

function todayKey() { return new Date().toLocaleDateString('en-CA'); }

export function loadStoredPlan(date) {
  try { return JSON.parse(localStorage.getItem(PREFIX + (date || todayKey())) || 'null'); } catch { return null; }
}

function saveStoredPlan(date, plan) {
  try {
    localStorage.setItem(PREFIX + date, JSON.stringify(plan));
    // GC plans older than KEEP_DAYS
    const cutoff = Date.now() - KEEP_DAYS * 86400000;
    Object.keys(localStorage).forEach(k => {
      if (!k.startsWith(PREFIX)) return;
      const d = k.slice(PREFIX.length);
      const t = new Date(d).getTime();
      if (!isNaN(t) && t < cutoff) localStorage.removeItem(k);
    });
  } catch {}
}

function isTrainingDayToday() {
  try {
    const log = JSON.parse(localStorage.getItem('sirat-workouts') || '[]');
    const todayStr = new Date().toLocaleDateString();
    return log.some(w => w?.timestamp && new Date(w.timestamp).toLocaleDateString() === todayStr);
  } catch { return false; }
}

// Sum today's already-logged food across real meal slots. Mirrors the
// extractTopFoods exclusion list (drinks/supplements don't count toward
// food intake) and skips entries the user already filtered out.
function extractConsumedToday(calorieLog) {
  const day = (calorieLog || {})[todayKey()] || {};
  const meals = day?.meals || {};
  const out = { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, items: [] };
  Object.entries(meals).forEach(([slot, arr]) => {
    if (EXCLUDED_SLOTS.has(slot)) return;
    (arr || []).forEach(e => {
      if (!e?.name) return;
      if (BEVERAGE_RE.test(e.name)) return;
      out.kcal      += +e.calories || 0;
      out.protein_g += +e.protein  || 0;
      out.carbs_g   += +e.carbs    || 0;
      out.fat_g     += +e.fat      || 0;
      out.items.push({
        name: String(e.name).slice(0, 40),
        kcal: Math.round(+e.calories || 0),
        protein_g: +((+e.protein || 0).toFixed(1)),
        loggedAt: e.loggedAt || null,
      });
    });
  });
  out.kcal      = Math.round(out.kcal);
  out.protein_g = +out.protein_g.toFixed(1);
  out.carbs_g   = +out.carbs_g.toFixed(1);
  out.fat_g     = +out.fat_g.toFixed(1);
  return out;
}

function buildPayload({ calorieLog, calorieGoals, wizardProfile }) {
  const aiTarget = loadAITarget();
  const macro_target = aiTarget ? {
    kcal: aiTarget.dailyTarget,
    protein_g: Math.round(aiTarget.macros.protein_g),
    carbs_g: Math.round(aiTarget.macros.carbs_g),
    fat_g: Math.round(aiTarget.macros.fat_g),
  } : {
    kcal: calorieGoals?.calories || 2000,
    protein_g: calorieGoals?.protein || 150,
    carbs_g: calorieGoals?.carbs || 225,
    fat_g: calorieGoals?.fat || 65,
  };
  const consumed_today = extractConsumedToday(calorieLog);
  const remaining_target = {
    kcal:      Math.max(0, macro_target.kcal      - consumed_today.kcal),
    protein_g: Math.max(0, +(macro_target.protein_g - consumed_today.protein_g).toFixed(1)),
    carbs_g:   Math.max(0, +(macro_target.carbs_g   - consumed_today.carbs_g  ).toFixed(1)),
    fat_g:     Math.max(0, +(macro_target.fat_g     - consumed_today.fat_g    ).toFixed(1)),
  };
  const now = new Date();
  const current_time = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  return {
    macro_target,
    consumed_today,
    remaining_target,
    current_time,
    top_foods: extractTopFoods(calorieLog, 90, 15),
    eating_window: extractEatingWindow(calorieLog, 30),
    training_day: isTrainingDayToday(),
    preferences: { halal: true, region: 'South Africa' },
    profile: {
      age: parseInt(wizardProfile?.age) || null,
      sex: wizardProfile?.sex || null,
    },
  };
}

// ── Module-level singleton generator ─────────────────────────────────────
// Survives component unmount: if the user taps Generate and immediately
// leaves the Plan tab, the Gemini call keeps running and the result lands
// in localStorage. Mounted components subscribe to know when the call
// finishes, so returning to the tab picks up the new plan automatically.
let _planInFlight = false;
let _planLastError = null;
const _planListeners = new Set();
function _planNotify() { _planListeners.forEach(fn => { try { fn(); } catch {} }); }

async function _runGenerate(payload) {
  if (_planInFlight) return;
  _planInFlight = true;
  _planLastError = null;
  _planNotify();
  try {
    try { publishSources("meal-plan", mealPlanSources(payload)); } catch {}
    const raw = await callGemini(JSON.stringify(payload), {
      systemInstruction: SYSTEM_PROMPT,
      responseSchema: SCHEMA,
      model: 'gemini-2.5-pro',
      maxOutputTokens: 16384,
      temperature: 0.6,
      feature: 'mealPlan',
    });
    const parsed = JSON.parse(raw);
    const record = { ...parsed, generatedAt: new Date().toISOString(), date: todayKey() };
    saveStoredPlan(record.date, record);
    try { hapticHayahSuccess(); } catch {}
  } catch (e) {
    console.warn('[MealPlan] generate failed', e);
    const msg = e.message || '';
    const isNetwork = /load failed|failed to fetch|networkerror/i.test(msg);
    _planLastError = isNetwork
      ? 'No internet — check your connection and try again'
      : (msg || 'Generate failed');
    try { hapticError(); } catch {}
  } finally {
    _planInFlight = false;
    _planNotify();
  }
}

// ── Component ────────────────────────────────────────────────────────────
export default function MealPlanView({ colors, calorieLog, calorieGoals, wizardProfile, showToast }) {
  const { text, muted, border, s1, s2, gold, teal, accent, danger, darkMode } = colors;
  const [plan, setPlan] = useState(() => loadStoredPlan());
  const [loading, setLoading] = useState(() => _planInFlight);
  const [error, setError] = useState(() => _planLastError);

  // Subscribe to module-level generator. Re-reads the stored plan whenever
  // the in-flight state changes — so a generate that started in this mount
  // and resolved after the user left the tab still surfaces on return.
  useEffect(() => {
    const onChange = () => {
      setLoading(_planInFlight);
      setError(_planLastError);
      if (!_planInFlight) {
        const fresh = loadStoredPlan();
        if (fresh) setPlan(fresh);
      }
    };
    _planListeners.add(onChange);
    // Sync once on mount in case state changed between renders.
    onChange();
    return () => { _planListeners.delete(onChange); };
  }, []);

  // Toast on transitions from loading→done WITHIN this mount lifetime.
  const wasLoadingRef = React.useRef(loading);
  useEffect(() => {
    if (wasLoadingRef.current && !loading && !error && showToast) showToast('Plan ready');
    wasLoadingRef.current = loading;
  }, [loading, error, showToast]);

  const payload = useMemo(() => buildPayload({ calorieLog, calorieGoals, wizardProfile }), [calorieLog, calorieGoals, wizardProfile]);
  const enoughData = (payload.top_foods?.length || 0) >= 8 && payload.eating_window != null;

  const generate = useCallback(() => {
    if (_planInFlight) return;
    _runGenerate(payload);
  }, [payload]);

  // ── Styles ──────────────────────────────────────────────────────────────
  const card = { borderRadius: 14, border: `1px solid ${border}`, background: s1, padding: 14, marginBottom: 12 };
  const label = { fontSize: 10, color: muted, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase' };
  const numHi = { fontSize: 18, fontWeight: 800, color: gold, fontVariantNumeric:'tabular-nums' };

  // Compare plan totals against the REMAINING target (full target minus
  // what's already been logged today) — the AI plans to fill the gap, not
  // re-cover the whole day.
  const macroDelta = useMemo(() => {
    if (!plan?.totals) return null;
    const t = plan.totals;
    const m = payload.remaining_target || payload.macro_target;
    return {
      kcal: t.kcal - m.kcal,
      kcalPct: m.kcal ? Math.round((t.kcal / m.kcal) * 100) : 0,
      protein: t.protein_g - m.protein_g,
      proteinPct: m.protein_g ? Math.round((t.protein_g / m.protein_g) * 100) : 0,
    };
  }, [plan, payload.macro_target, payload.remaining_target]);

  return (
    <div style={{ padding:'12px 12px 80px' }}>
      {/* Header / generate */}
      <div style={{ ...card, padding: 14 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap: 10 }}>
          <div>
            <div style={{ fontSize: 13, color: text, fontWeight: 800 }}>Today's plan</div>
            <div style={{ fontSize: 11, color: muted, marginTop: 2, display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span>{plan ? `Generated ${new Date(plan.generatedAt).toLocaleTimeString(undefined,{hour:'2-digit',minute:'2-digit'})}` : 'Not generated yet'}</span>
              <AICostBadge feature="mealPlan" prefix="·" fontSize={10} color={muted} />
            </div>
          </div>
          {enoughData && (
            <button onClick={generate} disabled={loading}
                    style={{ padding:'8px 14px', borderRadius: 10, border:'none', background: loading ? s2 : gold, color: loading ? muted : '#000', fontSize: 12, fontWeight: 800, cursor: loading ? 'default' : 'pointer', fontFamily:'inherit' }}>
              {loading ? <AIAnalysingPipeline variant="inline" label="Generating plan" accent="amber" theme={{ amber: gold, teal: gold }} /> : (plan ? 'Regenerate' : 'Generate plan')}
            </button>
          )}
        </div>

        {!enoughData && (
          <div style={{ marginTop: 10, padding:'10px 12px', background: s2, border:`1px dashed ${border}`, borderRadius: 10, fontSize: 12, color: muted, lineHeight: 1.55 }}>
            Need at least 8 distinct foods in your last-90-day history and 5+ days of timestamped meal logs to learn your patterns. Keep logging meals (with timestamps) and check back.
          </div>
        )}

        {error && (
          <div style={{ marginTop: 10, padding:'8px 10px', background:'rgba(255,107,107,0.10)', border:`1px solid ${danger}55`, borderRadius: 8, color: danger, fontSize: 12 }}>
            Couldn't generate plan: {error}
          </div>
        )}
      </div>

      {loading && (
        <div style={{ ...card, padding: 14 }}>
          <AIAnalysingPipeline
            theme={{ ink1: text, ink3: text, ink4: muted, line: border, lineSoft: border, teal: gold, amber: gold }}
            label="Generating today's plan"
            computeLabel="Synthesising meals"
            feature="meal-plan"
            sources={[
              "Food history (90d)",
              "Meal-timing patterns",
              "Already eaten today",
              "Remaining macros",
            ]}
            accent="amber"
          />
        </div>
      )}

      {plan && (
        <>
          {/* Window banner */}
          <div style={{ ...card, padding:'12px 14px' }}>
            <div style={label}>EATING WINDOW</div>
            <div style={{ display:'flex', alignItems:'baseline', gap: 8, marginTop: 4 }}>
              <span style={numHi}>{plan.eating_window.start}</span>
              <span style={{ color: muted, fontSize: 13 }}>–</span>
              <span style={numHi}>{plan.eating_window.end}</span>
              <span style={{ color: muted, fontSize: 11, marginLeft: 6 }}>· {plan.eating_window.window_hours}hr feeding · {plan.eating_window.fast_hours}hr fast</span>
            </div>
            {payload.eating_window && (
              <div style={{ fontSize: 10.5, color: muted, marginTop: 4 }}>From your last {payload.eating_window.days_analyzed}d (avg first {payload.eating_window.first_meal_avg}, last {payload.eating_window.last_meal_avg})</div>
            )}
          </div>

          {/* Totals + fit */}
          <div style={{ ...card }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 10 }}>
              <div>
                <div style={label}>PLAN TOTAL</div>
                <div style={{ marginTop: 4 }}>
                  <div style={numHi}>{Math.round(plan.totals.kcal).toLocaleString()} <span style={{ color: muted, fontSize: 11, fontWeight: 700 }}>kcal</span></div>
                  <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>P {Math.round(plan.totals.protein_g)} · C {Math.round(plan.totals.carbs_g)} · F {Math.round(plan.totals.fat_g)}</div>
                </div>
              </div>
              <div>
                <div style={label}>{payload.consumed_today?.kcal > 0 ? 'REMAINING TARGET' : 'TARGET'}</div>
                <div style={{ marginTop: 4 }}>
                  {(() => {
                    const tgt = payload.remaining_target || payload.macro_target;
                    return (
                      <>
                        <div style={{ fontSize: 16, color: text, fontWeight: 700, fontVariantNumeric:'tabular-nums' }}>{Math.round(tgt.kcal).toLocaleString()} <span style={{ color: muted, fontSize: 11, fontWeight: 700 }}>kcal</span></div>
                        <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>P {Math.round(tgt.protein_g)} · C {Math.round(tgt.carbs_g)} · F {Math.round(tgt.fat_g)}</div>
                        {payload.consumed_today?.kcal > 0 && (
                          <div style={{ fontSize: 10, color: muted, marginTop: 4, fontFamily: 'monospace', letterSpacing: '0.04em' }}>
                            of {Math.round(payload.macro_target.kcal).toLocaleString()} · {Math.round(payload.consumed_today.kcal)} eaten
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 10, display:'flex', gap: 12, fontSize: 11, color: muted, fontFamily:'monospace', letterSpacing:'0.08em' }}>
              <span>FIT <strong style={{ color: Math.abs(plan.macro_fit_pct - 100) <= 5 ? teal : '#f0a86b' }}>{Math.round(plan.macro_fit_pct)}%</strong></span>
              <span>FAMILIAR <strong style={{ color: plan.familiar_pct >= 70 ? teal : '#f0a86b' }}>{Math.round(plan.familiar_pct)}%</strong></span>
              {macroDelta && (
                <span>P <strong style={{ color: Math.abs(macroDelta.proteinPct - 100) <= 5 ? teal : '#f0a86b' }}>{macroDelta.proteinPct}%</strong></span>
              )}
            </div>
          </div>

          {/* Meals */}
          {plan.meals.map((m, i) => (
            <div key={i} style={card}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 13, color: text, fontWeight: 800 }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: muted, marginTop: 2, fontFamily:'monospace', letterSpacing:'0.06em' }}>{m.suggested_time}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={numHi}>{Math.round(m.kcal).toLocaleString()} <span style={{ color: muted, fontSize: 11, fontWeight: 700 }}>kcal</span></div>
                  <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>P {Math.round(m.protein_g)} · C {Math.round(m.carbs_g)} · F {Math.round(m.fat_g)}</div>
                </div>
              </div>
              <div style={{ marginTop: 10, display:'flex', flexDirection:'column', gap: 6 }}>
                {(m.items || []).map((it, j) => (
                  <div key={j} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'6px 10px', background: s2, border:`1px solid ${border}`, borderRadius: 8 }}>
                    <div style={{ display:'flex', alignItems:'baseline', gap: 6, minWidth: 0 }}>
                      <span style={{ fontSize: 12.5, color: text, fontWeight: 700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{it.name}</span>
                      <span style={{ fontSize: 11, color: muted, whiteSpace:'nowrap' }}>· {it.qty}</span>
                      {it.familiar && <span style={{ fontSize: 9.5, color: teal, fontWeight: 800, letterSpacing:'0.06em', marginLeft: 4 }}>★</span>}
                    </div>
                    <span style={{ fontSize: 11.5, color: muted, fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap' }}>{Math.round(it.kcal)} kcal</span>
                  </div>
                ))}
              </div>
              {m.rationale && (
                <div style={{ marginTop: 8, fontSize: 11.5, color: muted, lineHeight: 1.5, fontStyle:'italic' }}>{m.rationale}</div>
              )}
            </div>
          ))}

          {/* Plan rationale */}
          {plan.rationale && (
            <div style={{ ...card, background: s2, borderColor: teal + '55' }}>
              <div style={{ ...label, color: teal }}>WHY THIS PLAN</div>
              <div style={{ marginTop: 6, fontSize: 12.5, color: text, lineHeight: 1.55 }}>{plan.rationale}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
